/**
 * useCreateTransactionsBulk Hook
 *
 * 거래 일괄 입력(A-7)의 저장을 담당한다.
 *
 * @why 이 기능이 해결하려는 진짜 문제는 "쓰기가 느리다"가 아니라
 * "한 건 저장할 때마다 목록 전체를 다시 읽어온다"였다.
 * 단건 등록을 N번 반복하면 전체 재조회도 N번 일어난다.
 * 여기서는 화면에 머문 채 N건을 모아 저장하고, 재조회는 마지막 홈 이동 1번만 일어난다.
 */

import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '@/core/logger'
import { describeError, toErrorMessage } from '@/core/errorDiagnostics'
import { transactionRepository } from '../repositories'
import type { BulkTransactionDraftRow } from '../types/BulkTransaction'
import {
  validateAll,
  type BulkValidationContext,
  type BulkValidationSummary,
} from '../utils/bulkTransactionValidator'
import { toTransactionInputs } from '../utils/bulkTransactionMapper'

export interface SaveAllParams {
  rows: BulkTransactionDraftRow[]
  /**
   * 최신 마스터 데이터를 다시 불러오는 함수
   *
   * @why 입력하는 동안 다른 사용자가 결제수단·카테고리·멤버를 바꿀 수 있다.
   * 화면의 선택지는 불러온 시점의 스냅샷이라, 저장 직전에 최신 값으로 다시 검증해야
   * 이미 사라진 항목을 참조한 거래가 저장되는 것을 막을 수 있다.
   */
  refreshContext: () => Promise<BulkValidationContext>
}

/**
 * 실패한 단계
 *
 * @why 같은 "저장 실패"라도 원인 위치가 다르면 사용자가 할 일이 다르다.
 * 재검증(읽기)에서 끊겼으면 저장은 시도조차 안 됐으니 그냥 다시 누르면 되고,
 * 저장(쓰기)에서 끊겼으면 이미 반영됐을 수 있어 목록 확인이 먼저다.
 * 로그를 볼 수 없는 태블릿에서 이 구분이 유일한 단서가 된다.
 */
export type BulkSaveStage = 'refresh' | 'save'

export interface BulkSaveFailure {
  stage: BulkSaveStage
  /** 사용자에게 보여줄 한 줄 */
  message: string
  /** 화면에 띄울 진단 정보 (복사·캡처용) */
  detail: string
  /**
   * 실패했지만 실제로는 저장됐을 수 있는 상태
   *
   * @why Repository가 반영 여부를 확인해 재전송까지 시도하지만,
   * 확인 자체가 실패하면 알 수 없는 채로 남는다. 그 경우에만 true다.
   */
  mayHaveSaved: boolean
}

export interface SaveAllResult {
  ok: boolean
  /** 저장된 건수 (실패 시 0) */
  savedCount: number
  /** 저장 직전 재검증 결과. 오류로 막혔다면 어느 행이 문제인지 여기에 담긴다 */
  summary?: BulkValidationSummary
  /** 요청이 실패한 경우의 상세. 검증으로 막힌 경우에는 없다 */
  failure?: BulkSaveFailure
}

export interface UseCreateTransactionsBulkReturn {
  saveAll: (params: SaveAllParams) => Promise<SaveAllResult>
  loading: boolean
  error: string | null
  /**
   * 저장이 실패했지만 실제로는 저장됐을 수 있는 상태
   *
   * @why 요청이 서버에 도달한 뒤 응답만 유실돼도 실패로 보인다.
   * 이 경우 그대로 다시 저장하면 거래가 두 벌 쌓이므로, 사용자에게 목록 확인을 권한다.
   */
  mayHaveSaved: boolean
}

export function useCreateTransactionsBulk(): UseCreateTransactionsBulkReturn {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mayHaveSaved, setMayHaveSaved] = useState(false)

  /**
   * 저장 진행 중 여부 (이중 제출 차단용)
   *
   * @why loading 상태만으로는 막을 수 없다. 상태 갱신은 다음 렌더에 반영되므로
   * 같은 렌더 안에서 연속 호출되면 둘 다 통과한다.
   * append는 같은 내용을 두 번 보내면 두 벌이 쌓이므로(비멱등) ref로 즉시 차단한다.
   */
  const savingRef = useRef(false)

  const saveAll = useCallback(
    async ({ rows, refreshContext }: SaveAllParams): Promise<SaveAllResult> => {
      if (savingRef.current) {
        return { ok: false, savedCount: 0 }
      }

      savingRef.current = true
      setLoading(true)
      setError(null)
      setMayHaveSaved(false)

      const startedAt = Date.now()

      /**
       * 실패 단계 추적
       *
       * @why 어느 요청에서 끊겼는지는 catch에 도달한 뒤에는 알 수 없다.
       * 각 단계에 들어가기 직전에 표시해 두어야 진단에 남길 수 있다.
       */
      let stage: BulkSaveStage = 'refresh'

      try {
        // 1) 최신 마스터로 재검증 (동시 편집 방어)
        const context = await refreshContext()
        const summary = validateAll(rows, context)

        if (!summary.canSave) {
          return { ok: false, savedCount: 0, summary }
        }

        // 2) 검증을 통과한 행만 저장용 값으로 변환
        const inputs = toTransactionInputs(rows, summary)

        if (inputs.length === 0) {
          return { ok: false, savedCount: 0, summary }
        }

        // 3) 단일 요청으로 저장 (통째로 성공하거나 통째로 실패)
        stage = 'save'
        const created = await transactionRepository.createMany(inputs)

        logger.info({
          message: '📝 거래 일괄 저장 완료',
          count: created.length,
        })

        // 4) 홈으로 이동. 목록은 이때 한 번만 다시 읽는다.
        //    (건별 저장이 아니라 배치 저장이므로 전체 재조회가 N번이 아닌 1번)
        navigate('/')

        return { ok: true, savedCount: created.length, summary }
      } catch (err) {
        const message = toErrorMessage(err, '거래 저장에 실패했습니다')

        /**
         * 재검증(읽기) 단계의 실패는 저장을 시도조차 하지 않은 상태다.
         *
         * @why 이 구분이 없으면 모든 실패에 "이미 저장됐을 수 있다"는 경고가 붙어,
         * 사용자가 매번 목록을 확인하고 오게 된다. 경고가 늘 뜨면 아무도 안 읽는다.
         */
        const mayHaveSaved = stage === 'save'

        const detail = describeError(err, {
          stage,
          rowCount: rows.length,
          elapsedMs: Date.now() - startedAt,
        })

        setError(message)
        setMayHaveSaved(mayHaveSaved)

        logger.error(`거래 일괄 저장 실패 (${stage}): ${message}`, err)

        return {
          ok: false,
          savedCount: 0,
          failure: { stage, message, detail, mayHaveSaved },
        }
      } finally {
        savingRef.current = false
        setLoading(false)
      }
    },
    [navigate]
  )

  return { saveAll, loading, error, mayHaveSaved }
}
